import React from "react";
import { Link } from "react-router-dom";
import { FileText, Scale, AlertCircle } from "lucide-react";
import logo from "../assets/images/logo.jpg";

export default function TermsOfService() {
  // Scroll to top on page load
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logo}
              alt="Dotti Verify"
              className="w-10 h-10 rounded-lg"
            />
            <span className="text-xl font-bold text-gray-800">
              Dotti Verify
            </span>
          </Link>
          <Link
            to="/"
            className="text-gray-600 hover:text-[#007AFF] transition flex items-center gap-1"
          >
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-purple-100 p-4 rounded-full">
              <FileText className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                Terms of Service
              </h1>
              <p className="text-gray-500 mt-2">Last Updated: March 2026</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-amber-800 text-sm flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>
                These Terms of Service govern your use of the Dotti Verify
                platform. By using our website or services, you agree to comply
                with these terms.
              </span>
            </p>
          </div>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {/* Section 1 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                1
              </span>
              <span>Use of the Service</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                Dotti Verify provides online verification and related digital
                services. Users must use the platform only for lawful purposes
                and in accordance with these terms.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                2
              </span>
              <span>User Accounts</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 mb-4">
                Users may be required to create an account to access certain
                features. You are responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li className="pl-2">
                  Maintaining the confidentiality of your account information
                </li>
                <li className="pl-2">
                  All activities that occur under your account
                </li>
              </ul>
              <p className="text-gray-600 mt-4">
                Dotti Verify reserves the right to suspend or terminate accounts
                that violate these terms.
              </p>
            </div>
          </div>

          {/* Section 3 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                3
              </span>
              <span>Payments</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                Some services on Dotti Verify may require payment. Payments are
                processed through third-party payment providers. By making a
                payment, you agree to their processing policies.
              </p>
            </div>
          </div>

          {/* Section 4 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                4
              </span>
              <span>Prohibited Activities</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 mb-4">Users must not:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li className="pl-2">
                  Use the platform for illegal activities
                </li>
                <li className="pl-2">
                  Attempt to gain unauthorized access to the system
                </li>
                <li className="pl-2">
                  Interfere with the operation or security of the service
                </li>
                <li className="pl-2">
                  Provide false or misleading information
                </li>
              </ul>
              <p className="text-gray-600 mt-4">
                Violation of these rules may result in suspension or termination
                of access.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                5
              </span>
              <span>Service Availability</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                Dotti Verify aims to keep the platform available and functional
                at all times. However, we do not guarantee uninterrupted service
                and may perform maintenance or updates when necessary.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                6
              </span>
              <span>Limitation of Liability</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                Dotti Verify is not liable for any indirect or consequential
                damages resulting from the use or inability to use the platform.
              </p>
            </div>
          </div>

          {/* Section 7 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                7
              </span>
              <span>Changes to the Service</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                We may modify, update, or discontinue parts of the service at
                any time without prior notice.
              </p>
            </div>
          </div>

          {/* Section 8 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                8
              </span>
              <span>Changes to Terms</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                Dotti Verify reserves the right to update these Terms of Service
                at any time. Continued use of the platform after updates means
                you accept the revised terms.
              </p>
            </div>
          </div>

          {/* Section 9 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">
                9
              </span>
              <span>Governing Law</span>
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                These terms are governed by the laws applicable in the
                jurisdiction where Dotti Verify operates.
              </p>
            </div>
          </div>
        </div>

        {/* Acceptance Banner */}
        <div className="mt-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-lg p-6 text-white">
          <div className="flex items-center gap-4">
            <Scale className="w-8 h-8 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-lg mb-1">
                By using Dotti Verify, you agree to these terms
              </h3>
              <p className="text-white/90 text-sm">
                If you have any questions about these Terms of Service, please
                contact our support team.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Dotti Verify. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link
              to="/PrivacyPolicy"
              className="text-purple-600 hover:underline"
            >
              Privacy Policy
            </Link>
            <span>|</span>
            <Link
              to="/TermsOfServices"
              className="text-purple-600 hover:underline"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
